import { Component, Input, forwardRef, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UtilService } from '../../../../services/util.service';
import { OpenDialog } from '../../../../../app/shared/dialogs/dialog';
import { PdfImagePreviewComponent } from './pdf-image-preview/pdf-image-preview.component';

export interface UploadedFile {
  name: string;
  size: number;
  type: string;
  lastModified: number;
  url?: string;
  id?: string;
  uploadProgress?: number;
  isUploading?: boolean;
  uploadError?: string;
  previewUrl?: SafeResourceUrl;
  file?: File;
}

@Component({
  selector: 'app-document-upload',
  templateUrl: './document-upload.component.html',
  styleUrls: ['./document-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DocumentUploadComponent),
      multi: true,
    },
  ],
})
export class DocumentUploadComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label: string = '';
  @Input() name: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() multiple: boolean = false;
  @Input() accept: string = '*/*';
  @Input() maxFileSize: number = 3 * 1024 * 1024; // 3MB default
  @Input() maxFiles: number = 5;
  @Input() class: string = '';
  @Input() id: string = '';
  @Input() placeholder: string = 'Choose files or drag and drop here';
  @Input() showPreview: boolean = true;
  @Input() allowedTypes: string[] = [];
  @Input() formControl: FormControl = new FormControl();
  @Input() icon: string = '⬆️';
  @Input() uploadLocation: string = 'prdSignUp'; // Default upload location
  @Input() documentType: string = 'NIC';
  @Input() productSignupId: string = '';
  @Input() additionalData: { [key: string]: any } = {};
  @Input() enablePreview: boolean = true;
  @Output() fileSelected = new EventEmitter<UploadedFile[]>();
  @Output() fileRemoved = new EventEmitter<UploadedFile>();
  @Output() uploadError = new EventEmitter<string>();

  value: UploadedFile[] = [];
  touched: boolean = false;
  isDragOver: boolean = false;
  uploadProgress: { [key: string]: number } = {};
  errors: { [key: string]: boolean } = {};
  private destroy$ = new Subject<void>();

  // ControlValueAccessor implementation
  onChange = (value: UploadedFile[] | any) => {};
  onTouched = () => {};

  constructor(
    private utilService: UtilService,
    private domSanitizer: DomSanitizer,
    private router: Router,
    private cdRef: ChangeDetectorRef
  ) {}

  writeValue(value: UploadedFile[] | any): void {
    console.log('writeValue called with:', value);
    if (Array.isArray(value)) {
      this.value = value;
      // console.log('Set value from array in writeValue:', this.value);
    } else if (value && typeof value === 'object' && value.url) {
      // Convert object format back to array format for display
      this.value = [
        {
          name: value.note || 'Uploaded file',
          size: 0,
          type: 'application/octet-stream',
          lastModified: Date.now(),
          url: value.url,
          id: this.generateFileId(),
          isUploading: false,
          uploadProgress: 100,
        },
      ];
      // console.log('Set value from object in writeValue:', this.value);
      // Load base64 preview if URL exists
      if (value.url) {
        this.loadBase64Preview(value.url);
      }
    } else {
      this.value = [];
      console.log('Set empty value array in writeValue');
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit(): void {
    if (this.formControl) {
      // Sync with external form control
      this.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
        if (Array.isArray(value)) {
          this.value = value;
        } else if (value && typeof value === 'object' && value.url) {
          // Convert object format back to array format for display
          this.value = [
            {
              name: value.note || 'Uploaded file',
              size: 0,
              type: 'application/octet-stream',
              lastModified: Date.now(),
              url: value.url,
              id: this.generateFileId(),
              isUploading: false,
              uploadProgress: 100,
            },
          ];
          // Load base64 preview if URL exists
          if (value.url) {
            this.loadBase64Preview(value.url);
          }
        } else {
          this.value = [];
        }
      });

      // Set initial value
      // console.log('Initial formControl.value:', this.formControl.value);
      if (this.formControl.value) {
        if (Array.isArray(this.formControl.value)) {
          this.value = this.formControl.value;
          // console.log('Set value from array:', this.value);
        } else if (this.formControl.value && typeof this.formControl.value === 'object' && this.formControl.value.url) {
          // Convert object format back to array format for display
          this.value = [
            {
              name: this.formControl.value.note || 'Uploaded file',
              size: 0,
              type: 'application/octet-stream',
              lastModified: Date.now(),
              url: this.formControl.value.url,
              id: this.generateFileId(),
              isUploading: false,
              uploadProgress: 100,
            },
          ];
          // Load base64 preview if URL exists
          if (this.formControl.value.url) {
            this.loadBase64Preview(this.formControl.value.url);
          }
        } else {
          // console.log('Form control value exists but no URL found:', this.formControl.value);
        }
      } else {
        // console.log('No form control value found');
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFileSelected(event: any): void {
    const files = Array.from(event.target.files) as File[];
    this.processFiles(files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = Array.from(event.dataTransfer?.files || []);
    this.processFiles(files);
  }

  private processFiles(files: File[]): void {
    if (this.disabled) return;

    const validFiles: UploadedFile[] = [];
    const errors: string[] = [];
    this.errors = {}; // Clear previous errors

    files.forEach((file) => {
      // Check file size
      if (file.size > this.maxFileSize) {
        const errorMsg = `${file.name} is too large. Maximum size is ${this.formatFileSize(this.maxFileSize)}`;
        errors.push(errorMsg);
        this.errors['fileSizeTooLarge'] = true;
        return;
      }
      // Check file type if allowedTypes is specified
      if (this.allowedTypes.length > 0 && !this.allowedTypes.includes(file.type)) {
        const errorMsg = `${file.name} is not an allowed file type`;
        errors.push(errorMsg);
        this.errors['fileTypeNotAllowed'] = true;
        this.cdRef.detectChanges();
        return;
      }

      // Check if we've reached max files limit
      if (!this.multiple && this.value.length >= 1) {
        errors.push('Only one file is allowed');
        this.errors['onlyOneFileAllowed'] = true;
        return;
      }

      if (this.multiple && this.value.length >= this.maxFiles) {
        errors.push(`Maximum ${this.maxFiles} files allowed`);
        this.errors['maximumFilesAllowed'] = true;
        return;
      }

      const uploadedFile: UploadedFile = {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        id: this.generateFileId(),
        file: file,
        isUploading: true,
        uploadProgress: 0,
      };

      validFiles.push(uploadedFile);

      // Upload file to server
      this.uploadFileToServer(uploadedFile);
    });

    // Emit errors if any
    if (errors.length > 0) {
      this.uploadError.emit(errors.join(', '));
      // Clear errors after 5 seconds
      setTimeout(() => {
        this.errors = {};
      }, 5000);
    } else {
      // Clear errors if no errors occurred
      this.errors = {};
    }

    // Add valid files to the value array immediately for display
    if (validFiles.length > 0) {
      const newValue = this.multiple ? [...this.value, ...validFiles] : validFiles;
      this.value = newValue;
      this.fileSelected.emit(validFiles);
    }
  }

  removeFile(file: UploadedFile): void {
    this.value = this.value.filter((f) => f.id !== file.id);

    // Call onChange with the structured object for form control
    if (this.value && this.value.length > 0) {
      const formValue = {
        attachmentType: this.documentType,
        docType: this.documentType,
        note: this.label,
        url: this.value[0].url,
      };
      this.onChange(formValue);
    } else {
      this.onChange(null);
    }

    this.onTouched();

    // Update external form control
    if (this.formControl && this.value && this.value.length > 0) {
      let newVal = {
        attachmentType: this.documentType,
        docType: this.documentType,
        note: this.label,
        url: this.value[0].url,
      };
      this.formControl.setValue(newVal);
    } else if (this.formControl) {
      // If no files, set empty value
      this.formControl.setValue(null);
    }

    this.fileRemoved.emit(file);
  }

  private generateFileId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(fileType: string): string {
    if (fileType.startsWith('image/')) return 'image';
    if (fileType.includes('pdf')) return 'picture_as_pdf';
    if (fileType.includes('word') || fileType.includes('document')) return 'description';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'table_chart';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return 'slideshow';
    return 'insert_drive_file';
  }

  onBlur(): void {
    this.touched = true;
    this.onTouched();
  }

  trackByFileId(index: number, file: UploadedFile): string {
    return file.id || index.toString();
  }

  private async uploadFileToServer(uploadedFile: UploadedFile): Promise<void> {
    if (!uploadedFile.file) return;

    let progressInterval: any;

    try {
      // Create FormData for upload
      const uploadImageData = new FormData();
      uploadImageData.append('attachment', uploadedFile.file, uploadedFile.file.name);

      // Add required fields for product signup
      if (this.uploadLocation === 'prdSignUp') {
        uploadImageData.append('documentType', this.documentType);
        uploadImageData.append('productSignupId', this.productSignupId);
        uploadImageData.append('fileDataJson', null);
      }

      // Add additional data
      Object.keys(this.additionalData).forEach((key) => {
        uploadImageData.append(key, this.additionalData[key]);
      });

      // Update progress to show upload starting
      uploadedFile.uploadProgress = 5;
      uploadedFile.isUploading = true;

      // Simulate progress updates for better UX
      progressInterval = setInterval(() => {
        if (uploadedFile.uploadProgress < 90) {
          uploadedFile.uploadProgress += Math.random() * 10;
        }
      }, 200);

      // Use utilService for upload
      let data;
      if (this.uploadLocation === 'prdSignUp') {
        data = await this.utilService.uploadSignUpAttachments(uploadImageData);
      } else if (this.uploadLocation === 'ipo') {
        data = await this.utilService.uploadaunauthorizedImage(uploadImageData);
      } else if (this.uploadLocation === 'public-uploads') {
        data = await this.utilService.publicUploads(uploadImageData);
      } else if (this.uploadLocation === 'equity-requests') {
        data = await this.utilService.uploadPrivateFile(uploadImageData);
      } else {
        throw new Error('Invalid upload location');
      }

      // Clear progress interval
      clearInterval(progressInterval);

      // Update progress to complete
      uploadedFile.uploadProgress = 100;
      uploadedFile.isUploading = false;

      // Handle response based on upload location
      if (this.uploadLocation === 'prdSignUp') {
        uploadedFile.url = data['s3URL'];
        if (data['base64']) {
          uploadedFile.previewUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(data['base64'] + '#toolbar=0&navpanes=0&scrollbar=0');
        }
      } else if (this.uploadLocation === 'ipo') {
        uploadedFile.url = data.url?.split('?')[0];
        // For IPO files, generate preview using the URL directly
        if (data.url) {
          uploadedFile.previewUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(data.url);
        }
      } else if (this.uploadLocation === 'public-uploads') {
        uploadedFile.url = data.url?.split('?')[0];
        // For public uploads, generate preview using the URL directly
        if (data.url) {
          uploadedFile.previewUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(data.url);
        }
      } else if (this.uploadLocation === 'equity-requests') {
        uploadedFile.url = data.url;
        if (data.base64) {
          uploadedFile.previewUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(data.base64 + '#toolbar=0&navpanes=0&scrollbar=0');
        }
      }

      // Generate preview if enabled and not already set
      if (this.enablePreview && !uploadedFile.previewUrl) {
        this.generatePreview(uploadedFile);
      }

      // Update form control
      this.updateFormControl();
    } catch (error: any) {
      // Clear progress interval on error
      if (progressInterval) {
        clearInterval(progressInterval);
      }

      uploadedFile.isUploading = false;
      uploadedFile.uploadError = error.message || 'Upload failed';
      this.uploadError.emit(`Failed to upload ${uploadedFile.name}: ${error.message}`);
    }
  }

  async loadBase64Preview(s3Url: string): Promise<void> {
    try {
      let base64 = await this.utilService.getBase64StreamUsingS3(s3Url);
      base64 = `data:image/jpg;base64,${base64}`;
      if (this.value && this.value.length > 0) {
        this.value[0].previewUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(base64 + '#toolbar=0&navpanes=0&scrollbar=0');
      }
    } catch (error) {
      console.warn('Could not load base64 preview for file:', error);
    }
  }

  generatePreview(uploadedFile: UploadedFile): void {
    if (uploadedFile.type.startsWith('image/')) {
      // For images, create blob URL
      if (uploadedFile.file) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          uploadedFile.previewUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(e.target.result);
        };
        reader.readAsDataURL(uploadedFile.file);
      }
    } else if (uploadedFile.url) {
      // For other files, create preview URL
      uploadedFile.previewUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(uploadedFile.url);
    }
  }

  updateFormControl(): void {
    if (this.formControl && this.value && this.value.length > 0) {
      let newVal = {
        attachmentType: this.documentType,
        docType: this.documentType,
        note: this.label,
        url: this.value[0].url,
      };
      this.formControl.setValue(newVal);
    }
  }

  async openPreview(file) {
    // Use PdfImagePreviewComponent for popup
    if (file.url) {
      try {
        await OpenDialog(PdfImagePreviewComponent, {
          type: file.url.split('/').pop(),
          s3Url: file.url,
        });
      } catch (error) {
        console.warn('Preview dialog error:', error);
        // Fallback to new tab if dialog fails
        window.open(file.url, '_blank');
      }
    }
  }

  async previewFile(file: UploadedFile): Promise<void> {
    if (file.url) {
      await OpenDialog(PdfImagePreviewComponent, {
        type: file.url.split('/').pop(),
        s3Url: file.url,
      });
    }
  }
}
